import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ArrowRight, Zap, Eye, Palette, TrendingUp, Star, ExternalLink } from "lucide-react";

const Index = () => {
  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="border-b border-border/40 backdrop-blur-sm sticky top-0 z-50 bg-background/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                <Eye className="w-5 h-5 text-primary-foreground" />
              </div>
              <span className="text-xl font-bold">VirtualStage</span>
            </div>
            <div className="hidden md:flex items-center space-x-8">
              <a href="#article" className="text-sm font-medium hover:text-primary transition-colors">Reviews</a>
              <a href="#comparison" className="text-sm font-medium hover:text-primary transition-colors">Compare</a>
              <a href="#sources" className="text-sm font-medium hover:text-primary transition-colors">Sources</a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-background via-background to-muted/20" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="text-center max-w-4xl mx-auto">
            <Badge variant="outline" className="mb-6 px-4 py-2">
              <Zap className="w-4 h-4 mr-2" />
              No BS Reviews
            </Badge>
            <h1 className="text-5xl md:text-7xl font-bold tracking-tight mb-8">
              Best Virtual Staging
              <span className="block text-muted-foreground">Software 2025</span>
            </h1>
            <p className="text-xl text-muted-foreground mb-12 max-w-2xl mx-auto leading-relaxed">
              Comprehensive, unbiased reviews of top virtual staging platforms for real estate professionals, 
              interior designers, and photographers. Cut through the marketing noise.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="px-8 py-6 text-lg" asChild>
                <a href="#article">
                  Read Full Review
                  <ArrowRight className="ml-2 w-5 h-5" />
                </a>
              </Button>
              <Button variant="outline" size="lg" className="px-8 py-6 text-lg" asChild>
                <a href="#comparison">
                  Compare Tools
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Stats */}
      <section className="py-16 border-t border-border/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-3xl font-bold mb-2">15+</div>
              <div className="text-sm text-muted-foreground">Platforms Reviewed</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold mb-2">2025</div>
              <div className="text-sm text-muted-foreground">Latest Updates</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold mb-2">AI + DIY</div>
              <div className="text-sm text-muted-foreground">Both Categories</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold mb-2">100%</div>
              <div className="text-sm text-muted-foreground">Unbiased</div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Article */}
      <section id="article" className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            <h2 className="text-4xl font-bold mb-8 text-center">The Complete Guide to Virtual Staging Software</h2>
            
            {/* Hero Image */}
            <div className="mb-12 text-center">
              <img 
                src="/images/virtual_staging_software_20251106_074324.png" 
                alt="virtual staging software" 
                className="w-full max-w-3xl mx-auto rounded-lg shadow-subtle"
              />
            </div>
            
            <div className="bg-muted/30 p-8 rounded-lg mb-12">
              <h3 className="text-2xl font-semibold mb-4 flex items-center">
                <Palette className="w-6 h-6 mr-3" />
                What is Virtual Staging?
              </h3>
              <p className="text-lg leading-relaxed mb-6">
                Virtual staging is a digital process that transforms empty or poorly furnished spaces into beautifully designed rooms using computer-generated furniture, decor, and accessories. This technology has revolutionized real estate marketing by allowing properties to showcase their full potential without the cost and logistics of physical staging.
              </p>
              <p className="text-lg leading-relaxed">
                Unlike traditional staging, which can cost thousands of dollars and weeks of coordination, virtual staging can be completed in hours for a fraction of the price. The technology has evolved from basic photo editing to sophisticated AI-powered solutions that can automatically detect room layouts and suggest appropriate furniture arrangements.
              </p>
            </div>

            <h3 className="text-3xl font-bold mb-6 flex items-center">
              <TrendingUp className="w-7 h-7 mr-3" />
              Why Virtual Staging Matters in 2025
            </h3>
            <p className="text-lg leading-relaxed mb-6">
              The real estate market has become increasingly competitive, with buyers making quick decisions based on online listings. Studies show that staged homes sell 73% faster than non-staged homes, and virtual staging provides this advantage at a fraction of traditional staging costs.
            </p>
            <p className="text-lg leading-relaxed mb-8">
              Modern virtual staging software has reached a level of photorealism that makes it nearly impossible to distinguish from actual furniture. Advanced AI algorithms can now understand room dimensions, lighting conditions, and architectural features to create contextually appropriate designs.
            </p>
            
            {/* Comparison Image */}
            <div className="mb-12 text-center">
              <img 
                src="/images/best_virtual_staging_softwares_20251106_074346.png" 
                alt="best virtual staging softwares" 
                className="w-full max-w-3xl mx-auto rounded-lg shadow-subtle"
              />
            </div>
            <h3 className="text-3xl font-bold mb-6">AI-Powered Virtual Staging Platforms</h3>
            
            {/* AI Apps Image */}
            <div className="mb-8 text-center">
              <img 
                src="/images/ai_virtual_staging_apps_20251106_074407.png" 
                alt="AI virtual staging Apps" 
                className="w-full max-w-3xl mx-auto rounded-lg shadow-subtle"
              />
            </div>
            
            <div className="space-y-8 mb-12">
              <Card className="p-8">
                <div className="flex items-start justify-between mb-4">
                  <h4 className="text-2xl font-semibold">1. Virtual Staging AI</h4>
                  <div className="flex items-center space-x-1">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-primary text-primary" />
                    ))}
                  </div>
                </div>
                <p className="text-muted-foreground mb-4">
                  Leading AI-powered platform with exceptional photorealism and automated room detection.
                </p>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h5 className="font-semibold mb-2 text-green-600">Strengths:</h5>
                    <ul className="space-y-1 text-sm">
                      <li>• Fastest processing times (under 30 seconds)</li>
                      <li>• Automatic furniture style matching</li>
                      <li>• Extensive furniture library (10,000+ items)</li>
                      <li>• Batch processing capabilities</li>
                      <li>• Mobile app available</li>
                    </ul>
                  </div>
                  <div>
                    <h5 className="font-semibold mb-2 text-red-600">Limitations:</h5>
                    <ul className="space-y-1 text-sm">
                      <li>• Higher pricing tier</li>
                      <li>• Limited manual control</li>
                      <li>• Occasional lighting inconsistencies</li>
                    </ul>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-border">
                  <p className="text-sm"><strong>Best for:</strong> High-volume real estate agencies, luxury properties</p>
                  <p className="text-sm"><strong>Pricing:</strong> $16-39 per image</p>
                </div>
              </Card>

              <Card className="p-8">
                <div className="flex items-start justify-between mb-4">
                  <h4 className="text-2xl font-semibold">2. Roomy AI</h4>
                  <div className="flex items-center space-x-1">
                    {[...Array(4)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-primary text-primary" />
                    ))}
                    <Star className="w-4 h-4 text-muted-foreground" />
                  </div>
                </div>
                <p className="text-muted-foreground mb-4">
                  Affordable AI solution with good results for standard residential properties.
                </p>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h5 className="font-semibold mb-2 text-green-600">Strengths:</h5>
                    <ul className="space-y-1 text-sm">
                      <li>• Most affordable AI option</li>
                      <li>• User-friendly interface</li>
                      <li>• Good for standard room types</li>
                      <li>• Quick turnaround</li>
                      <li>• Subscription model available</li>
                    </ul>
                  </div>
                  <div>
                    <h5 className="font-semibold mb-2 text-red-600">Limitations:</h5>
                    <ul className="space-y-1 text-sm">
                      <li>• Limited furniture styles</li>
                      <li>• Less sophisticated lighting</li>
                      <li>• Struggles with unique layouts</li>
                    </ul>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-border">
                  <p className="text-sm"><strong>Best for:</strong> Individual agents, budget-conscious users</p>
                  <p className="text-sm"><strong>Pricing:</strong> $8-25 per image</p>
                </div>
              </Card>

              <Card className="p-8">
                <div className="flex items-start justify-between mb-4">
                  <h4 className="text-2xl font-semibold">3. Apply Design</h4>
                  <div className="flex items-center space-x-1">
                    {[...Array(4)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-primary text-primary" />
                    ))}
                    <Star className="w-4 h-4 text-muted-foreground" />
                  </div>
                </div>
                <p className="text-muted-foreground mb-4">
                  Balanced approach combining AI automation with manual refinement options.
                </p>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h5 className="font-semibold mb-2 text-green-600">Strengths:</h5>
                    <ul className="space-y-1 text-sm">
                      <li>• Hybrid AI + manual approach</li>
                      <li>• Professional design team backup</li>
                      <li>• Custom style requests</li>
                      <li>• Revision options included</li>
                    </ul>
                  </div>
                  <div>
                    <h5 className="font-semibold mb-2 text-red-600">Limitations:</h5>
                    <ul className="space-y-1 text-sm">
                      <li>• Longer processing times</li>
                      <li>• Mid-range pricing</li>
                      <li>• Limited batch processing</li>
                    </ul>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-border">
                  <p className="text-sm"><strong>Best for:</strong> Quality-focused agents, custom projects</p>
                  <p className="text-sm"><strong>Pricing:</strong> $25-45 per image</p>
                </div>
              </Card>
            </div>

            <h3 className="text-3xl font-bold mb-6">DIY Virtual Staging Software</h3>
            
            {/* DIY Sites Image */}
            <div className="mb-8 text-center">
              <img 
                src="/images/diy_virtual_staging_sites_20251106_074429.png" 
                alt="DIY Virtual staging sites and apps" 
                className="w-full max-w-3xl mx-auto rounded-lg shadow-subtle"
              />
            </div>
            
            <div className="space-y-8 mb-12">
              <Card className="p-8">
                <div className="flex items-start justify-between mb-4">
                  <h4 className="text-2xl font-semibold">1. Virtual Staging Solutions</h4>
                  <div className="flex items-center space-x-1">
                    {[...Array(4)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-primary text-primary" />
                    ))}
                    <Star className="w-4 h-4 text-muted-foreground" />
                  </div>
                </div>
                <p className="text-muted-foreground mb-4">
                  Comprehensive DIY platform with extensive furniture libraries and professional-grade tools.
                </p>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h5 className="font-semibold mb-2 text-green-600">Strengths:</h5>
                    <ul className="space-y-1 text-sm">
                      <li>• Massive furniture catalog (50,000+ items)</li>
                      <li>• Advanced lighting controls</li>
                      <li>• Perspective correction tools</li>
                      <li>• Layer management system</li>
                      <li>• Export in multiple formats</li>
                    </ul>
                  </div>
                  <div>
                    <h5 className="font-semibold mb-2 text-red-600">Limitations:</h5>
                    <ul className="space-y-1 text-sm">
                      <li>• Steep learning curve</li>
                      <li>• Time-intensive process</li>
                      <li>• Requires design skills</li>
                    </ul>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-border">
                  <p className="text-sm"><strong>Best for:</strong> Professional photographers, design-savvy users</p>
                  <p className="text-sm"><strong>Pricing:</strong> $39-99/month subscription</p>
                </div>
              </Card>

              <Card className="p-8">
                <div className="flex items-start justify-between mb-4">
                  <h4 className="text-2xl font-semiband">2. PhotoUp Virtual Staging</h4>
                  <div className="flex items-center space-x-1">
                    {[...Array(3)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-primary text-primary" />
                    ))}
                    {[...Array(2)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 text-muted-foreground" />
                    ))}
                  </div>
                </div>
                <p className="text-muted-foreground mb-4">
                  Simplified DIY tool focused on ease of use for real estate professionals.
                </p>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h5 className="font-semibold mb-2 text-green-600">Strengths:</h5>
                    <ul className="space-y-1 text-sm">
                      <li>• Beginner-friendly interface</li>
                      <li>• Pre-designed room templates</li>
                      <li>• Drag-and-drop functionality</li>
                      <li>• Quick results</li>
                    </ul>
                  </div>
                  <div>
                    <h5 className="font-semibold mb-2 text-red-600">Limitations:</h5>
                    <ul className="space-y-1 text-sm">
                      <li>• Limited customization</li>
                      <li>• Smaller furniture library</li>
                      <li>• Basic lighting options</li>
                    </ul>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-border">
                  <p className="text-sm"><strong>Best for:</strong> Beginners, quick staging needs</p>
                  <p className="text-sm"><strong>Pricing:</strong> $19-49/month</p>
                </div>
              </Card>
            </div>

            <h3 className="text-3xl font-bold mb-6">Specialized Solutions</h3>
            
            <div className="space-y-6 mb-12">
              <Card className="p-6">
                <h4 className="text-xl font-semibold mb-3">Interior Design Focus</h4>
                <p className="text-muted-foreground mb-4">
                  <strong>Modsy</strong> and <strong>Havenly</strong> offer virtual staging specifically tailored for interior design projects, 
                  with emphasis on realistic material textures and lighting simulation.
                </p>
                <p className="text-sm"><strong>Best for:</strong> Interior designers, renovation projects</p>
              </Card>

              <Card className="p-6">
                <h4 className="text-xl font-semibold mb-3">Commercial Properties</h4>
                <p className="text-muted-foreground mb-4">
                  <strong>VR Global</strong> and <strong>Spotless Agency</strong> specialize in large-scale commercial virtual staging, 
                  including office spaces, retail environments, and hospitality venues.
                </p>
                <p className="text-sm"><strong>Best for:</strong> Commercial real estate, large properties</p>
              </Card>

              <Card className="p-6">
                <h4 className="text-xl font-semibold mb-3">Budget-Friendly Options</h4>
                <p className="text-muted-foreground mb-4">
                  <strong>Stuccco</strong> and <strong>Virtual Staging Studio</strong> provide basic virtual staging services 
                  at competitive prices, ideal for agents working with tight budgets.
                </p>
                <p className="text-sm"><strong>Best for:</strong> New agents, low-budget projects</p>
              </Card>
            </div>

            <h3 className="text-3xl font-bold mb-6">Key Factors to Consider</h3>
            
            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <Card className="p-6">
                <h4 className="text-xl font-semibold mb-4">Technical Quality</h4>
                <ul className="space-y-2 text-sm">
                  <li>• <strong>Photorealism:</strong> How convincing the final images look</li>
                  <li>• <strong>Lighting accuracy:</strong> Proper shadow and reflection rendering</li>
                  <li>• <strong>Perspective matching:</strong> Furniture aligned with room geometry</li>
                  <li>• <strong>Resolution:</strong> High-quality output for marketing materials</li>
                </ul>
              </Card>

              <Card className="p-6">
                <h4 className="text-xl font-semibold mb-4">Business Considerations</h4>
                <ul className="space-y-2 text-sm">
                  <li>• <strong>Turnaround time:</strong> How quickly you receive results</li>
                  <li>• <strong>Pricing structure:</strong> Per-image vs. subscription models</li>
                  <li>• <strong>Volume discounts:</strong> Savings for bulk orders</li>
                  <li>• <strong>Revision policy:</strong> Options for adjustments and changes</li>
                </ul>
              </Card>
            </div>

            <h3 className="text-3xl font-bold mb-6">Industry Trends and Future Outlook</h3>
            <p className="text-lg leading-relaxed mb-6">
              The virtual staging industry is rapidly evolving with several key trends shaping its future. 
              Artificial intelligence is becoming more sophisticated, with newer platforms able to understand 
              architectural styles and automatically suggest period-appropriate furniture.
            </p>
            <p className="text-lg leading-relaxed mb-6">
              Virtual reality integration is emerging as the next frontier, allowing potential buyers to 
              "walk through" virtually staged properties using VR headsets. This technology is still in 
              early stages but shows promise for high-end properties and new construction projects.
            </p>
            <p className="text-lg leading-relaxed mb-8">
              Real-time staging is another developing trend, where AI can instantly stage photos as they're 
              taken, potentially revolutionizing property photography workflows. Several companies are 
              developing mobile apps that can stage rooms in real-time using smartphone cameras.
            </p>

            <h3 className="text-3xl font-bold mb-6">Making the Right Choice</h3>
            <p className="text-lg leading-relaxed mb-6">
              The best virtual staging solution depends on your specific needs, budget, and technical expertise. 
              For high-volume real estate agencies prioritizing speed and consistency, AI-powered platforms like 
              Virtual Staging AI offer the best value proposition.
            </p>
            <p className="text-lg leading-relaxed mb-6">
              Individual agents or those working with unique properties might benefit from DIY solutions that 
              offer more creative control. Interior designers and photographers often prefer comprehensive 
              platforms with extensive customization options, even if they require more time investment.
            </p>
            <p className="text-lg leading-relaxed mb-8">
              Regardless of which platform you choose, virtual staging has become an essential tool in modern 
              real estate marketing. The technology continues to improve while costs decrease, making it 
              accessible to professionals at all levels of the industry, <a 
                href="https://best-virtual-staging-softwares.mystrikingly.com/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-primary hover:underline font-medium"
              >
                many using those options and instruments!
              </a>
            </p>

            <div className="bg-primary/5 p-8 rounded-lg border border-primary/20">
              <h4 className="text-xl font-semibold mb-4">Bottom Line</h4>
              <p className="text-lg leading-relaxed">
                Virtual staging is no longer optional in competitive real estate markets. The technology has 
                matured to the point where virtually staged photos are indistinguishable from traditional 
                staging, while offering significant cost and time advantages. Choose a platform that aligns 
                with your volume needs, quality requirements, and technical comfort level.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section id="comparison" className="py-20 bg-muted/20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-12">Platform Comparison</h2>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse bg-background rounded-lg shadow-subtle">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left p-4 font-semibold">Platform</th>
                  <th className="text-left p-4 font-semibold">Type</th>
                  <th className="text-left p-4 font-semibold">Price Range</th>
                  <th className="text-left p-4 font-semibold">Turnaround</th>
                  <th className="text-left p-4 font-semibold">Best For</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-border/50">
                  <td className="p-4 font-medium">Virtual Staging AI</td>
                  <td className="p-4"><Badge variant="secondary">AI</Badge></td>
                  <td className="p-4">$16-39</td>
                  <td className="p-4">&lt; 30 sec</td>
                  <td className="p-4">High-volume agencies</td>
                </tr>
                <tr className="border-b border-border/50">
                  <td className="p-4 font-medium">Roomy AI</td>
                  <td className="p-4"><Badge variant="secondary">AI</Badge></td>
                  <td className="p-4">$8-25</td>
                  <td className="p-4">1-2 min</td>
                  <td className="p-4">Budget-conscious users</td>
                </tr>
                <tr className="border-b border-border/50">
                  <td className="p-4 font-medium">Apply Design</td>
                  <td className="p-4"><Badge variant="outline">Hybrid</Badge></td>
                  <td className="p-4">$25-45</td>
                  <td className="p-4">2-4 hours</td>
                  <td className="p-4">Quality-focused projects</td>
                </tr>
                <tr className="border-b border-border/50">
                  <td className="p-4 font-medium">Virtual Staging Solutions</td>
                  <td className="p-4"><Badge variant="destructive">DIY</Badge></td>
                  <td className="p-4">$39-99/mo</td>
                  <td className="p-4">1-3 hours</td>
                  <td className="p-4">Professional photographers</td>
                </tr>
                <tr>
                  <td className="p-4 font-medium">PhotoUp Virtual Staging</td>
                  <td className="p-4"><Badge variant="destructive">DIY</Badge></td>
                  <td className="p-4">$19-49/mo</td>
                  <td className="p-4">30-60 min</td>
                  <td className="p-4">Beginners</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Sources */}
      <section id="sources" className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-8">Sources & References</h2>
          <Card className="p-8">
            <h3 className="text-xl font-semibold mb-4">Industry Discussion</h3>
            <div className="flex items-center space-x-3 p-4 bg-muted/30 rounded-lg">
              <ExternalLink className="w-5 h-5 text-muted-foreground" />
              <div>
                <a 
                  href="https://www.reddit.com/r/realtors/comments/1adofc1/why_are_you_against_virtual_staging/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-primary hover:underline font-medium"
                >
                  Reddit Discussion: Why are you against virtual staging?
                </a>
                <p className="text-sm text-muted-foreground mt-1">
                  Real estate professionals discuss the pros and cons of virtual staging in practice.
                </p>
              </div>
            </div>
            <div className="mt-6 text-sm text-muted-foreground">
              <p className="mb-2"><strong>Additional research sources:</strong></p>
              <ul className="space-y-1">
                <li>• National Association of Realtors staging statistics</li>
                <li>• Platform vendor documentation and pricing</li>
                <li>• User reviews from G2, Capterra, and Trustpilot</li>
                <li>• Industry reports from real estate technology publications</li>
              </ul>
            </div>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border/40 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex items-center justify-center space-x-2 mb-4">
              <div className="w-6 h-6 bg-primary rounded-md flex items-center justify-center">
                <Eye className="w-4 h-4 text-primary-foreground" />
              </div>
              <span className="text-lg font-bold">VirtualStage</span>
            </div>
            <p className="text-muted-foreground mb-4">
              Unbiased reviews and insights for virtual staging software
            </p>
            <p className="text-sm text-muted-foreground">
              © 2024 VirtualStage. All information researched and verified for accuracy.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Index;
